/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  NOTIFICATIONSYSTEM.TSX - Push Notifications & Toast Alerts               ║
 * ║  Browser notifications, in-app toasts, and real-time alerts               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Notification types
type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'whale' | 'reaper' | 'win';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  requestPermission: () => Promise<boolean>;
  hasPermission: boolean;
  sendBrowserNotification: (title: string, body: string, icon?: string) => void;
  notificationSettings: NotificationSettings;
  updateSettings: (settings: Partial<NotificationSettings>) => void;
}

interface NotificationSettings {
  browserNotifications: boolean;
  inAppToasts: boolean;
  soundEnabled: boolean;
  whaleAlerts: boolean;
  timerAlerts: boolean;
  purchaseConfirmations: boolean;
  withdrawConfirmations: boolean;
}

const defaultSettings: NotificationSettings = {
  browserNotifications: false,
  inAppToasts: true,
  soundEnabled: true,
  whaleAlerts: true,
  timerAlerts: true,
  purchaseConfirmations: true,
  withdrawConfirmations: true,
};

const NotificationContext = createContext<NotificationContextType | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

// Icons for notification types
const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️',
  whale: '🐋',
  reaper: '💀',
  win: '🏆',
};

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  success: 'border-green-500 bg-green-500/10',
  error: 'border-red-500 bg-red-500/10',
  warning: 'border-yellow-500 bg-yellow-500/10',
  info: 'border-blue-500 bg-blue-500/10',
  whale: 'border-neon-cyan bg-neon-cyan/10',
  reaper: 'border-energy-purple bg-energy-purple/10',
  win: 'border-yellow-400 bg-yellow-400/10',
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [hasPermission, setHasPermission] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem('nt3d_notification_settings');
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  });

  // Check permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setHasPermission(Notification.permission === 'granted');
    }
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('nt3d_notification_settings', JSON.stringify(settings));
  }, [settings]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setHasPermission(granted);
      if (granted) {
        setSettings(prev => ({ ...prev, browserNotifications: true }));
      }
      return granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  const sendBrowserNotification = useCallback((title: string, body: string, icon?: string) => {
    if (!hasPermission || !settings.browserNotifications) return;

    try {
      new Notification(title, {
        body,
        icon: icon || '/logo.jpg',
        badge: '/logo.jpg',
        tag: 'nt3d-notification',
        requireInteraction: false,
      });
    } catch (error) {
      console.error('Error sending browser notification:', error);
    }
  }, [hasPermission, settings.browserNotifications]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    if (!settings.inAppToasts) return;

    const id = Math.random().toString(36).substring(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: Date.now(),
      duration: notification.duration ?? 5000,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, [settings.inAppToasts]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const updateSettings = useCallback((newSettings: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // Listen for game events
  useEffect(() => {
    const handleGameActivity = (event: CustomEvent) => {
      const { type, isWhale, amount, keys, player } = event.detail;
      
      const shortAddr = player ? `${player.slice(0, 6)}...${player.slice(-4)}` : '';
      
      // Format keys properly (keys are stored as bigint, divide by 1e18 if needed, or just convert to number)
      const formatKeys = (k: bigint | number | undefined): string => {
        if (k === undefined) return '0';
        const num = typeof k === 'bigint' ? Number(k) : k;
        if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
        if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
        if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toLocaleString();
      };
      
      const keyCount = formatKeys(keys);

      if (isWhale && settings.whaleAlerts) {
        addNotification({
          type: 'whale',
          title: '�‍⬛ Raven Alert!',
          message: `${shortAddr} just bought ${keyCount} keys!`,
          duration: 8000,
        });
        sendBrowserNotification('Raven Alert!', `Big buy: ${keyCount} keys`);
      } else if (type === 'purchase' && settings.purchaseConfirmations) {
        addNotification({
          type: 'success',
          title: 'Keys Purchased!',
          message: `You bought ${keyCount} keys successfully`,
          duration: 4000,
        });
      } else if (type === 'withdraw' && settings.withdrawConfirmations) {
        // Format PAW amount (amount is in wei as bigint)
        const formatPaw = (a: bigint | number): string => {
          const num = typeof a === 'bigint' ? Number(a) / 1e18 : a;
          if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
          if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
          if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
          if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
          return num.toFixed(2);
        };
        addNotification({
          type: 'success',
          title: 'Dividends Claimed!',
          message: `You claimed ${formatPaw(amount)} PAW`,
          duration: 4000,
        });
      } else if (type === 'reaper') {
        addNotification({
          type: 'reaper',
          title: '💀 REAPER KEY!',
          message: `${shortAddr} triggered the Reaper!`,
          duration: 10000,
        });
        sendBrowserNotification('REAPER KEY!', 'Someone triggered the Reaper Key!');
      } else if (type === 'round_end') {
        addNotification({
          type: 'win',
          title: '🏆 Round Ended!',
          message: 'A new round is starting...',
          duration: 10000,
        });
        sendBrowserNotification('Round Ended!', 'Check if you won!');
      }
    };

    window.addEventListener('game-activity', handleGameActivity as EventListener);
    return () => window.removeEventListener('game-activity', handleGameActivity as EventListener);
  }, [addNotification, sendBrowserNotification, settings]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    requestPermission,
    hasPermission,
    sendBrowserNotification,
    notificationSettings: settings,
    updateSettings,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
}

// Notification Toast Container
function NotificationContainer({ 
  notifications, 
  onRemove 
}: { 
  notifications: Notification[]; 
  onRemove: (id: string) => void;
}) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      <AnimatePresence mode="popLayout">
        {notifications.map(notification => (
          <motion.div
            key={notification.id}
            layout
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            className={`p-4 rounded-xl border-2 backdrop-blur-md shadow-2xl ${
              NOTIFICATION_COLORS[notification.type]
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{NOTIFICATION_ICONS[notification.type]}</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-orbitron text-sm font-bold text-white">
                  {notification.title}
                </h4>
                <p className="text-xs text-gray-300 font-rajdhani mt-1">
                  {notification.message}
                </p>
                {notification.action && (
                  <button
                    onClick={notification.action.onClick}
                    className="mt-2 text-xs text-neon-cyan hover:underline font-rajdhani"
                  >
                    {notification.action.label}
                  </button>
                )}
              </div>
              <button
                onClick={() => onRemove(notification.id)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// Notification Settings Panel
export function NotificationSettings() {
  const { 
    notificationSettings, 
    updateSettings, 
    hasPermission, 
    requestPermission 
  } = useNotifications();

  const toggleSetting = (key: keyof NotificationSettings) => {
    updateSettings({ [key]: !notificationSettings[key] });
  };

  return (
    <div className="glass-panel-3d p-6">
      <h3 className="text-xl font-orbitron text-neon-cyan mb-4 flex items-center gap-2">
        🔔 Notification Settings
      </h3>

      <div className="space-y-4">
        {/* Browser Notifications */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-rajdhani">Browser Notifications</p>
            <p className="text-xs text-gray-500">Get notified even when not on the page</p>
          </div>
          {hasPermission ? (
            <ToggleSwitch
              checked={notificationSettings.browserNotifications}
              onChange={() => toggleSetting('browserNotifications')}
            />
          ) : (
            <button
              onClick={requestPermission}
              className="px-3 py-1 text-xs bg-neon-cyan/20 text-neon-cyan rounded-lg hover:bg-neon-cyan/30 transition-colors"
            >
              Enable
            </button>
          )}
        </div>

        {/* In-App Toasts */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-rajdhani">In-App Toasts</p>
            <p className="text-xs text-gray-500">Show notification popups</p>
          </div>
          <ToggleSwitch
            checked={notificationSettings.inAppToasts}
            onChange={() => toggleSetting('inAppToasts')}
          />
        </div>

        <div className="border-t border-neon-purple/20 pt-4 space-y-4">
          <p className="text-sm text-gray-400 font-rajdhani">Alert Types</p>

          <div className="flex items-center justify-between">
            <p className="text-white font-rajdhani">�‍⬛ Raven Alerts</p>
            <ToggleSwitch
              checked={notificationSettings.whaleAlerts}
              onChange={() => toggleSetting('whaleAlerts')}
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-white font-rajdhani">⏱️ Timer Alerts</p>
            <ToggleSwitch
              checked={notificationSettings.timerAlerts}
              onChange={() => toggleSetting('timerAlerts')}
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-white font-rajdhani">✅ Purchase Confirmations</p>
            <ToggleSwitch
              checked={notificationSettings.purchaseConfirmations}
              onChange={() => toggleSetting('purchaseConfirmations')}
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-white font-rajdhani">💰 Withdraw Confirmations</p>
            <ToggleSwitch
              checked={notificationSettings.withdrawConfirmations}
              onChange={() => toggleSetting('withdrawConfirmations')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Toggle Switch Component
function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked ? 'bg-neon-cyan' : 'bg-gray-700'
      }`}
    >
      <motion.div
        className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg"
        animate={{ left: checked ? '28px' : '4px' }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />
    </button>
  );
}

